"use client";

import Link from "next/link";
import Image from "next/image";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { Heart } from "lucide-react";
import { toast } from "@/lib/toast";

export function FavoritesPageClient() {
  const data = useQuery(api.customers.customerDashboard.getMyFavorites, { limit: 50 });
  const removeFavorite = useMutation(api.customers.customerDashboard.removeFavorite);

  if (data === undefined) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <LoadingSpinner branded />
      </div>
    );
  }

  const favorites = data.favorites || [];
  const total = data.total || 0;

  const handleRemove = async (favoriteId: Id<"favorites">) => {
    try {
      await removeFavorite({ favoriteId });
      toast.success("Removed from wishlist");
    } catch (e: any) {
      toast.error(e.message || "Failed to remove");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl md:text-3xl font-bold" style={{ color: "var(--admin-text-primary)" }}>
          My Wishlist
        </h1>
        {total > 0 && (
          <span
            className="text-sm px-3 py-1 rounded-full font-medium"
            style={{ backgroundColor: "var(--admin-bg-tertiary)", color: "var(--admin-text-secondary)" }}
          >
            {total} item{total !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Grid */}
      {favorites.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favorites.map((fav: any) => {
            const product = fav.product;
            if (!product) return null;

            const imageUrl = product.images?.[0];

            return (
              <div
                key={fav._id}
                className="rounded-2xl border shadow-sm overflow-hidden group transition-all hover:shadow-md"
                style={{ backgroundColor: "var(--admin-bg-secondary)", borderColor: "var(--admin-border)" }}
              >
                {/* Image */}
                <div className="relative aspect-square overflow-hidden" style={{ backgroundColor: "var(--admin-bg-tertiary)" }}>
                  {imageUrl ? (
                    <Image
                      src={imageUrl}
                      alt={product.name}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Heart className="w-12 h-12 opacity-30" style={{ color: "var(--admin-text-muted)" }} />
                    </div>
                  )}

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(fav._id)}
                    className="absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-all hover:scale-110"
                    style={{ backgroundColor: "rgba(255,255,255,0.8)" }}
                    title="Remove from wishlist"
                  >
                    <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                  </button>
                </div>

                {/* Info */}
                <div className="p-4 space-y-2">
                  <p className="font-medium truncate" title={product.name} style={{ color: "var(--admin-text-primary)" }}>
                    {product.name}
                  </p>
                  <p className="text-lg font-bold" style={{ color: "var(--admin-text-primary)" }}>
                    UGX {product.price?.toLocaleString()}
                  </p>
                  <Link
                    href={`/product/${product.slug}`}
                    className="block w-full text-center py-2 text-sm font-medium rounded-xl text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "var(--admin-accent)" }}
                  >
                    View Product
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="rounded-2xl p-12 text-center border border-dashed"
          style={{ backgroundColor: "var(--admin-bg-tertiary)", borderColor: "var(--admin-border)" }}
        >
          <Heart className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--admin-text-muted)" }} />
          <p className="font-medium mb-1" style={{ color: "var(--admin-text-primary)" }}>Your wishlist is empty</p>
          <p className="text-sm mb-4" style={{ color: "var(--admin-text-secondary)" }}>
            Save items you love to your wishlist so you can find them easily later.
          </p>
          <Link
            href="/"
            className="inline-block px-6 py-2.5 text-sm font-medium rounded-xl text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "var(--admin-accent)" }}
          >
            Start Shopping
          </Link>
        </div>
      )}
    </div>
  );
}
